import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../api";
import { isResponseSuccess } from "../utils";
import { URLS } from "../constants";
import { BillItem, BillType, ITables, KITCHEN_ITEM_STATUS, TABLE_STATUS } from "../all-types";

/**
 * Fetch all tables from the backend.
 */
export const getTableList = createAsyncThunk(
  "tables/getTableList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_TABLES);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch tables");
      }
      return response.data.data as ITables[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch tables");
    }
  }
);


export const getTableById = createAsyncThunk(
  "tables/getTableById",
  async (tableId: string, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_TABLE(tableId));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch table");
      }
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch table");
    }
  }
);

/**
 * Create a new table entry.
 */
export const addRestaurantTable = createAsyncThunk(
  "tables/addRestaurantTable",
  async (table: Omit<ITables, 'tableId'>, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post(URLS.ADD_TABLE, { table });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to add table");
      }
      dispatch(getTableList()); // Refresh list after adding
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not add new table");
    }
  }
);

/**
 * Update an existing table.
 */
export const updateRestaurantTable = createAsyncThunk(
  "tables/updateRestaurantTable",
  async (details: { id: string; table: Partial<ITables> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put(URLS.UPDATE_TABLE(details.id), { table: details.table });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table");
      }
      dispatch(getTableList()); // Refresh list after updating
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update table");
    }
  }
);

/**
 * Delete a table.
 */
export const deleteRestaurantTable = createAsyncThunk(
  "tables/deleteRestaurantTable",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(URLS.DELETE_TABLE(id));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to delete table");
      }
      dispatch(getTableList()); // Refresh list after deleting
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not delete table");
    }
  }
);



export const updateCartItemOfTheTable = createAsyncThunk(
  "tables/updateCartItemOfTheTable",
  async (
    details: { table: ITables},
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.put(
        URLS.UPDATE_TABLE(details.table?._id || ""),
        { table: details.table }
      );

      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table");
      }

      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update table");
    }
  }
);


export const updateTableItemStatus = createAsyncThunk(
  "tables/updateTableItemStatus",
  async (
    details: { tableId: string, sno: number, status: KITCHEN_ITEM_STATUS },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.put(
        URLS.UPDATE_TABLE_ITEM(details.tableId, details.sno, details.status)
      );

      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table item status");
      }

      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update table item status");
    }
  }
);
